import { Box, Typography, Stack } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import CustomSkeleton from '../../Skeleton/CustomSkeleton';
import {
  convertToCurrency,
  convertToDollarLabel,
  convertToCurrencyWithoutSign,
  convertToCurrencyWithPercent,
  convertToPercentageWithDecimal,
  convertToRoundOffWithNumberOfDecimals,
  convertToRoundOffWithNumberOfDecimalsWithPercent
} from '../../../utils/Utils';
import { chartConfig } from '../../../utils/Config';
import {
  DOLLAR_FORMAT_COLUMNS,
  COMMA_FORMAT_COLUMNS,
  COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  ZERO_DECIMAL_FORMAT_COLUMNS,
  ONE_DECIMAL_FORMAT_COLUMNS,
  TWO_DECIMAL_FORMAT_COLUMNS,
  TWO_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT
} from '../../../utils/Constants';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function LineChart({ title, chartData, valueLabel, id, isLineChartLoading, page, timeframe  }) {
  const chartOptions = {
    responsive: true,
    animation: false,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          usePointStyle: false,
          pointStyle: 'rect',
          padding: 20,
          boxWidth: 38,
          boxHeight: 13
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (valueLabel === 'Profitpercentage' || valueLabel === 'Wastepercentage') {
              return value + '%';
            }
            return `$ ${Number(value)?.toLocaleString()}`;
          }
        }
      }
    },
    interaction: chartConfig,
    scales: {
      y: {
        beginAtZero: true,
        offset: 5,
        ticks: {
          callback: function (value) {
            if (['Revenue', 'RevenuePerSku', 'Profit']?.indexOf(valueLabel) > -1) {
              return convertToDollarLabel(value);
            }
            if (DOLLAR_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToCurrency(value);
            }
            if (COMMA_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToCurrencyWithoutSign(value);
            }
            if (COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToCurrencyWithPercent(value);
            }
            if (ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToPercentageWithDecimal(value, 0);
            }
            if (ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToPercentageWithDecimal(value, 1);
            }
            if (TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToPercentageWithDecimal(value, 2);
            }
            if (ZERO_DECIMAL_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimals(value, 0);
            }
            if (ONE_DECIMAL_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimals(value, 1);
            }
            if (TWO_DECIMAL_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimals(value, 2);
            }
            if (TWO_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimalsWithPercent(value, 2);
            }
            if (ONE_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimalsWithPercent(value, 1);
            }
          }
        },
        grid: {
          display: true
        }
      },
      x: {
        beginAtZero: true,
        grid: {
          display: false
        },
        title: {
          display: true,
          text: 'Period'
        }
      }
    }
  };

  let lineChartVal = {
    labels: chartData?.labels,
    datasets: [
      {
        label: page === 'Voting' ? 'Actual Year' : 'Current Year (' + timeframe.startTimePeriod + " - " + timeframe.endTimePeriod + ")",
        data: Object.keys(chartData)?.length !== 0 ? chartData?.datasets?.[1]?.data : [],
        borderColor: '#00B0F0',
        backgroundColor: '#B05788',
        tension: 0,
        borderWidth: 1
      },
      {
        label: page === 'Voting' ? 'Last Year' : 'Previous Year (' + (timeframe.startTimePeriod-100) + " - " + (timeframe.endTimePeriod-100) + ")",
        data: Object.keys(chartData)?.length !== 0 ? chartData?.datasets?.[0]?.data : [],
        borderColor: '#B05788',
        backgroundColor: '#00B0F0',
        tension: 0,
        borderWidth: 1
      }
    ]
  };

  return isLineChartLoading ? (
    <Box
      data-testid="custom-skeleton"
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 24px',
        borderRadius: '8px',
        border: '1px solid #DBEAFE',
        backgroundColor: '#F8FAFC',
        justifyContent: 'center', // Center horizontally
        alignItems: 'center', // Center vertically
        width: '100%',
        height: '200px'
      }}>
      <CustomSkeleton numberOfSkeleton={5} />
    </Box>
  ) : (
    <Box
      id={id}
      data-testid="line-chart-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 24px',
        borderRadius: '8px',
        border: '1px solid #DBEAFE',
        backgroundColor: '#F8FAFC',
        width: '100%',
        height: '-webkit-calc(100%)'
      }}>
      <Stack justifyContent={'space-between'} direction={'row'} gap={2}>
        <Typography variant="overline" sx={{ fontWeight: 'bold', color: '#374151' }}>
          {title}
        </Typography>
      </Stack>

      <Box sx={{ marginTop: '16px', width: '100%' }} data-testid="line-chart">
        <Line options={chartOptions} data={lineChartVal} />
      </Box>
    </Box>
  );
}

export default LineChart;
